// Decorative Forge stage art: a 70s sun on the horizon; light pulses travel
// along its rays, from the sun outward. Pure SVG + CSS (no runtime
// dependency); motion is disabled when the user prefers reduced motion.

const CENTER_X = 240;
const HORIZON_Y = 250;
const SUN_RADIUS = 92;
const RAY_INNER = 108;
const RAY_OUTER = 134;
const RAY_COUNT = 13;

const SUN_BANDS = [
  { y: 208, height: 5 },
  { y: 224, height: 7 },
  { y: 240, height: 10 },
];

// Rays spread over the visible half of the sun, from left to right.
const RAYS = Array.from({ length: RAY_COUNT }, (_, index) => {
  const angle = Math.PI + ((index + 0.5) * Math.PI) / RAY_COUNT;
  return {
    x1: CENTER_X + RAY_INNER * Math.cos(angle),
    y1: HORIZON_Y + RAY_INNER * Math.sin(angle),
    x2: CENTER_X + RAY_OUTER * Math.cos(angle),
    y2: HORIZON_Y + RAY_OUTER * Math.sin(angle),
    key: index,
  };
});

/**
 * `busy`: the sun as a loader (online diagnostic, 03/11): brighter rays, a quicker pulse sweeping
 * from left to right, a quicker glow, so the wait reads as work in progress.
 */
export const ForgeSunArt = ({ busy = false, className }: { busy?: boolean; className?: string }) => (
  <svg
    viewBox="0 0 480 280"
    className={className ?? 'h-auto w-full max-w-[440px]'}
    role="img"
    aria-label={busy ? 'Domino travaille' : 'Un soleil se lève sur l’horizon'}
  >
    <defs>
      <radialGradient id="forge-sun-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFD84D" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#FFD84D" stopOpacity="0" />
      </radialGradient>
      <mask id="forge-sun-bands">
        <rect x="0" y="0" width="480" height="280" fill="white" />
        {SUN_BANDS.map((band) => (
          <rect key={band.y} x="140" y={band.y} width="200" height={band.height} fill="black" />
        ))}
      </mask>
      <clipPath id="forge-sky">
        <rect x="0" y="0" width="480" height={HORIZON_Y} />
      </clipPath>
    </defs>

    <g clipPath="url(#forge-sky)">
      <circle
        cx={CENTER_X}
        cy={HORIZON_Y}
        r="190"
        fill="url(#forge-sun-glow)"
        className="motion-safe:animate-sunGlow"
        style={{ transformBox: 'fill-box', transformOrigin: 'center', ...(busy ? { animationDuration: '2.4s' } : {}) }}
      />
      {RAYS.map((ray) => (
        <g key={ray.key}>
          <line
            x1={ray.x1}
            y1={ray.y1}
            x2={ray.x2}
            y2={ray.y2}
            stroke="#F8C93A"
            strokeOpacity={busy ? 0.55 : 0.35}
            strokeWidth={busy ? 4 : 3}
            strokeLinecap="round"
          />
          <line
            x1={ray.x1}
            y1={ray.y1}
            x2={ray.x2}
            y2={ray.y2}
            pathLength={1}
            stroke="#F8C93A"
            strokeWidth={busy ? 4 : 3}
            strokeLinecap="round"
            strokeDasharray="0.45 1.45"
            strokeDashoffset="-1"
            className="motion-safe:animate-sunRayFlow"
            style={busy ? { stroke: '#F2A900', animationDuration: '1.6s', animationDelay: `${ray.key * 0.07}s` } : undefined}
          />
        </g>
      ))}
      <circle
        cx={CENTER_X}
        cy={HORIZON_Y}
        r={SUN_RADIUS}
        fill="#FFD84D"
        mask="url(#forge-sun-bands)"
      />
    </g>

    <line
      x1="40"
      y1={HORIZON_Y}
      x2="440"
      y2={HORIZON_Y}
      stroke="#4A1F4E"
      strokeOpacity="0.25"
      strokeWidth="1.5"
    />
  </svg>
);
